import React, { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, Checkbox, CircularProgress, FormControlLabel, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { shippingApi } from './api';

const localTime = () => {
  const date = new Date();
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};

// Desktop-only entry from the existing order detail. Every command is bound to
// the displayed package/revisions. No automatic eBay send follows handover.
export default function DispatchPanel({ shipmentId, capabilities, onAccessLost }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);
  const [revision, setRevision] = useState(0);
  const [carrier, setCarrier] = useState('');
  const [number, setNumber] = useState('');
  const [verification, setVerification] = useState('');
  const [checked, setChecked] = useState(false);
  const [handed, setHanded] = useState(false);
  const [reviewed, setReviewed] = useState(false);
  const [when, setWhen] = useState(localTime);
  const pending = useRef(null);
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  useEffect(() => {
    const controller = new AbortController();
    setData(null); setError('');
    shippingApi.dispatch(shipmentId, controller.signal).then((value) => {
      if (controller.signal.aborted) return;
      setData(value); setCarrier(value.approved_carriers?.[0] || '');
      setHanded(false); setReviewed(false); setChecked(false);
    }).catch((err) => {
      if (controller.signal.aborted) return;
      if (err.status === 401 || err.status === 403) onAccessLost(err);
      else setError(err.message || 'Dispatch details unavailable.');
    });
    return () => controller.abort();
  }, [shipmentId, revision, onAccessLost]);

  const submit = async (path, payload) => {
    if (busy) return;
    setBusy(true); setError(''); setNotice('');
    try {
      if (!pending.current) {
        if (!window.crypto?.getRandomValues) throw new Error('Secure request IDs require a supported browser over HTTPS.');
        const bytes = new Uint8Array(16); window.crypto.getRandomValues(bytes);
        pending.current = { path, payload: { ...payload, request_key: Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('') } };
      }
      const command = pending.current;
      await shippingApi.dispatchCommand(command.path, command.payload, capabilities.csrf_token);
      if (!mounted.current) return;
      pending.current = null;
      setNotice('Recorded. A queued operation is not an eBay confirmation.');
      setRevision((v) => v + 1);
    } catch (err) {
      if (!mounted.current) return;
      if (err.status === 401 || err.status === 403) onAccessLost(err);
      else setError(err.message || 'Outcome unknown. Retry this same request or reload the recorded state.');
    } finally { if (mounted.current) setBusy(false); }
  };
  const refresh = () => {
    pending.current = null; setNotice(''); setRevision((v) => v + 1);
  };
  const prefix = `shipments/${shipmentId}`;
  const expected = data ? {
    package_key: data.package_key, shipment_revision: data.shipment_revision,
    manifest_revision: data.manifest_revision, address_revision: data.address_revision,
  } : {};
  const locked = busy || !!pending.current;
  const op = data?.operation;
  const trackedRequired = data?.effective_service !== 'UNTRACKED_LETTER';
  return <Stack spacing={1} sx={{ mt: 2 }}>
    <Typography variant="subtitle1">Handover and eBay dispatch</Typography>
    {error && <Alert severity="error">{error}</Alert>}
    {notice && <Alert severity="info">{notice}</Alert>}
    {pending.current && <Button disabled={busy} onClick={() => submit()}>Retry same request</Button>}
    {!data ? (!error && <CircularProgress size={20} aria-label="Reading dispatch" />) : <>
      <Typography variant="body2">Physical: {data.physical_state} · eBay: {op?.status || 'Not queued'}</Typography>
      {op?.execution_mode === 'REHEARSAL' && <Alert severity="info">Simulation only. No eBay fulfilment is created.</Alert>}
      {op?.remote_fulfillment_id && <Typography variant="body2">Confirmed remote fulfilment: {op.remote_fulfillment_id}</Typography>}
      {op?.reason_code && <Alert severity="warning">{op.reason_code}{op.review_code ? ` · ${op.review_code}` : ''}</Alert>}
      {data.physical_state === 'OUTGOING' && capabilities?.dispatch?.handover && <>
        <Typography variant="body2">Effective service: {data.effective_service}. Record handover only after the package has actually been posted or given to the carrier.</Typography>
        {data.tracking ? <Typography variant="body2">Verified label: {data.tracking.carrier_code} · {data.tracking.tracking_number}</Typography> : <>
          <Typography variant="body2">{trackedRequired ? 'Verified tracking is required.' : 'This letter can be sent without tracking. Add tracking only if it is on the physical label.'}</Typography>
          {!!data.approved_carriers?.length && <Stack spacing={1}>
            <TextField select label="Carrier" value={carrier} onChange={(e) => setCarrier(e.target.value)} disabled={locked}>
              {data.approved_carriers.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
            </TextField>
            <TextField label="Tracking number on label" value={number} onChange={(e) => setNumber(e.target.value)} disabled={locked} inputProps={{ maxLength: 256 }} />
            <TextField label="Scan or enter tracking number again" value={verification} onChange={(e) => setVerification(e.target.value)} disabled={locked} inputProps={{ maxLength: 256 }} />
            <FormControlLabel control={<Checkbox checked={checked} disabled={locked} onChange={(e) => setChecked(e.target.checked)} />} label="I checked the carrier and number on this package’s label" />
            <Button disabled={locked || !checked || !/^[A-Za-z0-9]+$/.test(number) || number !== verification || !carrier}
              onClick={() => submit(`${prefix}/tracking`, { ...expected, carrier_code: carrier, tracking_number: number, verification_number: verification, label_checked: true })}>Save verified tracking</Button>
          </Stack>}
          {trackedRequired && !data.approved_carriers?.length && <Alert severity="warning">An administrator must enable the approved carrier before tracked handover.</Alert>}
        </>}
        <TextField label="Actual handover time (this device’s local time)" type="datetime-local" value={when} disabled={locked} InputLabelProps={{ shrink: true }} onChange={(e) => setWhen(e.target.value)} />
        <FormControlLabel control={<Checkbox checked={handed} disabled={locked} onChange={(e) => setHanded(e.target.checked)} />} label="This exact package has actually been handed over" />
        <Button variant="contained" disabled={locked || !handed || !when || !Number.isFinite(new Date(when).getTime()) || (trackedRequired && !data.tracking)}
          onClick={() => submit(`${prefix}/handover`, { ...expected, actually_handed_over: true, shipped_at: new Date(when).toISOString() })}>Record actual handover</Button>
      </>}
      {data.handover && <Typography variant="body2">Handed over: {data.handover.shipped_at} · {data.handover.tracking_number || 'Untracked letter'}</Typography>}
      {data.review && capabilities?.dispatch?.review && (!op || ['PENDING', 'BLOCKED', 'RETRY_WAIT'].includes(op.status)) && <Box>
        <Typography variant="subtitle2">Review exact package dispatch · {data.review.environment}</Typography>
        {data.review.body.lineItems.map((line) => <Typography key={line.lineItemId} variant="body2">Line {line.lineItemId}: {line.quantity} sale units</Typography>)}
        <Typography variant="body2">Shipped: {data.review.body.shippedDate} · {data.review.body.shippingCarrierCode || 'No carrier/tracking fields'} {data.review.body.trackingNumber || ''}</Typography>
        <Typography variant="caption" component="div">Operation: {data.review.operation_key}. Production requires this exact pilot key and separate deployment approval.</Typography>
        <FormControlLabel control={<Checkbox checked={reviewed} disabled={locked} onChange={(e) => setReviewed(e.target.checked)} />} label="I reviewed these line quantities, actual handover time and tracking" />
        <Button disabled={locked || !reviewed} onClick={() => submit(`${prefix}/queue-dispatch`, { payload_sha256: data.review.payload_sha256, confirm_actual_handover: true })}>Approve and queue this dispatch</Button>
      </Box>}
      {op?.status === 'UNKNOWN' && <Alert severity="warning">eBay may have accepted this package. Do not resend it or dispatch it manually again.
        {capabilities?.dispatch?.review && <Button disabled={locked} onClick={() => submit(`operations/${op.operation_key}/reconcile-dispatch`, { payload_sha256: op.payload_sha256 })}>Check eBay result (read only)</Button>}
      </Alert>}
      {op?.status === 'FAILED' && <Alert severity="warning">Review the failure before any correction. This view cannot edit or delete an eBay fulfilment.</Alert>}
    </>}
    <Button disabled={busy} onClick={refresh}>Reload handover / dispatch</Button>
  </Stack>;
}
